import React, { useState, useEffect } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import { get, getAllFixVersions } from '../../services/apiService';

// Register the required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * TestAutomationStatusChart component for displaying test case counts by automation status
 * @returns {JSX.Element} TestAutomationStatusChart component
 */
const TestAutomationStatusChart = () => {
  const [fixVersions, setFixVersions] = useState([]);
  const [selectedFixVersion, setSelectedFixVersion] = useState('');
  const [automationData, setAutomationData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [metadata, setMetadata] = useState(null);

  // Fetch fix versions on component mount
  useEffect(() => {
    fetchFixVersions();
  }, []);

  // Fetch automation data when fix version is selected
  useEffect(() => {
    if (selectedFixVersion) {
      fetchAutomationData();
    }
  }, [selectedFixVersion]);

  const fetchFixVersions = async () => {
    try {
      const response = await getAllFixVersions();
      
      // Process response similar to ReleasesView
      let activeVersionsData = [];
      
      if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        // Nested data structure (response.data.data is an array)
        activeVersionsData = response.data.data.filter(fixVersion => fixVersion.active);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Direct array in data property
        activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
      } else if (response && Array.isArray(response)) {
        // Direct array response
        activeVersionsData = response.filter(fixVersion => fixVersion.active);
      } else if (response && response.success && Array.isArray(response.data)) {
        // Success object with data array
        activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
      } else {
        console.error('Invalid fix versions response:', response);
        setError('Failed to load fix versions. Invalid response format.');
        return;
      }
      
      // Extract version strings and filter for valid versions (5.9.08 and above)
      const versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
      
      // Filter fix versions to only include those with format 5.9.08 and above
      const filteredVersions = versionStrings.filter(version => {
        const regex = /^(\d+)\.(\d+)\.(\d+)$/;
        const match = version.match(regex);
        if (!match) return false;
        
        const [, major, minor, patch] = match;
        // Check if it's 5.9.08 or higher
        return (
          parseInt(major) > 5 || 
          (parseInt(major) === 5 && parseInt(minor) > 9) || 
          (parseInt(major) === 5 && parseInt(minor) === 9 && parseInt(patch) >= 8)
        );
      });
      
      setFixVersions(filteredVersions);
      
      // Auto-select the first fix version if available
      if (filteredVersions.length > 0) {
        setSelectedFixVersion(filteredVersions[0]);
      }
    } catch (error) {
      console.error('Error fetching fix versions:', error);
      setError('Failed to fetch fix versions');
    }
  };

  const fetchAutomationData = async () => {
    if (!selectedFixVersion) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await get(
        `/api/workitems/public/test-automation-status?fixVersion=${encodeURIComponent(selectedFixVersion)}`
      );
      
      if (response.success) {
        setAutomationData(response.data);
        setMetadata(response.metadata);
      } else {
        setError(response.message || 'Failed to fetch automation data');
      }
    } catch (error) {
      console.error('Error fetching automation data:', error);
      setError('Failed to fetch test automation data');
    } finally {
      setLoading(false);
    }
  };

  const handleFixVersionChange = (event) => {
    setSelectedFixVersion(event.target.value);
  };

  // Prepare chart data
  const prepareChartData = () => {
    const statuses = Object.keys(automationData);
    const counts = Object.values(automationData);

    // Define colors for different automation statuses
    const statusColors = {
      'Automated': '#4caf50', // Green
      'Manual': '#2196f3', // Blue
      'TBD': '#ff9800', // Orange
      'Not Specified': '#9e9e9e', // Grey
      'Not Found': '#f44336', // Red
      'Requires Automation': '#673ab7', // Purple
      'Partially Automated': '#00bcd4', // Cyan
      'Cannot Automate': '#795548' // Brown
    };

    const backgroundColors = statuses.map(status => 
      statusColors[status] || '#9e9e9e'
    );

    return {
      labels: statuses,
      datasets: [{
        label: 'Test Case Count',
        data: counts,
        backgroundColor: backgroundColors,
        borderColor: 'transparent',
        borderWidth: 1
      }]
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Automation Status'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Test Cases'
        },
        ticks: {
          precision: 0 // Only show whole numbers
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: `Test Cases by Automation Status - ${selectedFixVersion}`,
        font: {
          size: 16
        }
      },
      legend: {
        display: false // Hide legend since we only have one dataset
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value} test cases`;
          }
        }
      }
    }
  };

  const hasData = Object.keys(automationData).length > 0;

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Fix Version Dropdown */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="fix-version-select-label">Release Version</InputLabel>
          <Select
            labelId="fix-version-select-label"
            id="fix-version-select"
            value={selectedFixVersion}
            label="Release Version"
            onChange={handleFixVersionChange}
            disabled={fixVersions.length === 0}
          >
            {fixVersions.map((version) => (
              <MenuItem key={version} value={version}>
                {version}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      )}

      {/* Chart Display */}
      {!loading && hasData && (
        <Box>
          <Box height="400px" width="100%">
            <Bar data={prepareChartData()} options={chartOptions} />
          </Box>
          
          {/* Metadata Display */}
          {metadata && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Unique Test Cases: {metadata.totalUniqueTestIds}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tests Found in Database: {metadata.testsFoundInDatabase}
              </Typography>
              {metadata.testsNotFoundInDatabase > 0 && (
                <Typography variant="body2" color="error">
                  Tests Not Found in Database: {metadata.testsNotFoundInDatabase}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Work Items: {metadata.workItemCounts.pbis} PBIs, {metadata.workItemCounts.features} Features, {metadata.workItemCounts.epics} Epics, {metadata.workItemCounts.productionBugs} Production Bugs, {metadata.workItemCounts.internalBugs} Internal Bugs
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* No Data Display */}
      {!loading && !hasData && selectedFixVersion && (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          height="300px"
          flexDirection="column"
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No test cases found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No test cases found for release version: {selectedFixVersion}
          </Typography>
        </Box>
      )}

      {/* No Fix Version Selected */}
      {!selectedFixVersion && fixVersions.length > 0 && (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          height="300px"
        >
          <Typography variant="body1" color="text.secondary">
            Please select a release version to view test automation status
          </Typography>
        </Box>
      )}

      {/* No Fix Versions Available */}
      {fixVersions.length === 0 && !loading && (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          height="300px"
        >
          <Typography variant="body1" color="text.secondary">
            No release versions available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TestAutomationStatusChart;
